import { useEffect, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript, javascriptLanguage } from "@codemirror/lang-javascript";
import { syntaxTree } from "@codemirror/language";
import { CompletionContext } from "@codemirror/autocomplete";
import { monokai } from "@uiw/codemirror-theme-monokai";
import useCodeMirrorState from "~/stores/code-mirror";
import type { CodeMirrorAutoCompletes } from "~/types/ui";
import { codeMirrorAutocompleteTypeEnum } from "~/utils/codeMirror-util";

const generateExtension = (autocompleteOptions: CodeMirrorAutoCompletes[]) => {
  return javascriptLanguage.data.of({
    autocomplete: function myCompletions(context: CompletionContext) {
      const nodeBefore = syntaxTree(context.state).resolveInner(
        context.pos,
        -1,
      );
      const isProperty =
        nodeBefore?.name === "PropertyName" || nodeBefore?.name === ".";

      const match = isProperty
        ? context.matchBefore(/\w+\.\w*/)
        : context.matchBefore(/\w+/);

      if (!match) return null;
      const options = autocompleteOptions
        .filter(
          (x) =>
            (isProperty
              ? x.type === codeMirrorAutocompleteTypeEnum.METHOD
              : x.type === codeMirrorAutocompleteTypeEnum.VARIABLE ||
                x.type === codeMirrorAutocompleteTypeEnum.DEVEUI_LOOKUP) &&
            x.label
              .toLocaleLowerCase()
              .startsWith(match.text.toLocaleLowerCase()),
        )
        .map(({ type, ...keepAttrs }) => keepAttrs);

      return {
        from: match?.from,
        options: options,
      };
    },
  });
};

export function CodeMirrorComponent({
  value,
  onChange,
  height = "300px",
  minHeight,
  maxHeight,
  notEditable = false,
}: {
  value: string;
  onChange?: (val: string) => void;
  height?: string | undefined;
  minHeight?: string | undefined;
  maxHeight?: string | undefined;

  notEditable?: boolean;
}) {
  const [localValue, setLocalValue] = useState<string>(value);
  const { autocompletions: autocompleteOptions } = useCodeMirrorState();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <CodeMirror
      value={localValue}
      height={height}
      minHeight={minHeight}
      maxHeight={maxHeight}
      theme={monokai}
      extensions={[javascript(), generateExtension(autocompleteOptions)]}
      onChange={(val, _) => {
        setLocalValue(val);
      }}
      onBlur={() => {
        if (onChange != undefined) onChange(localValue);
      }}
      editable={!notEditable}
    />
  );
}
